import React, { useRef, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Badge } from "./ui/badge";
import { useRole } from "../context/RoleContext";

const Receipts = () => {
  const { receipts } = useRole();
  const trackRef = useRef(null);
  const [active, setActive] = useState(null);

  const scrollBy = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const amount = Math.min(el.clientWidth * 0.8, 600);
    el.scrollBy({ left: dir * amount, behavior: "smooth" });
  };

  return (
    <section
      id="receipts"
      data-testid="receipts-section"
      className="section-pad border-t border-white/5 relative overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(700px 320px at 95% 5%, rgba(189, 195, 199,0.08), transparent 55%), radial-gradient(700px 320px at 0% 95%, rgba(236, 240, 241,0.08), transparent 55%)"
        }}
      />

      <div className="relative container-x">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <div className="eyebrow mb-3">§ The receipts</div>
            <h2 className="display text-[36px] md:text-[56px] leading-[0.98] text-[#FFF3E9]">
              No fluff. <br />
              Just{" "}
              <span className="marker-cyan serif-italic" style={{ color: "#FFF3E9", fontStyle: "italic" }}>
                track record.
              </span>
            </h2>
            <p className="mt-4 text-[13px] text-[#9A9AA0]">
              Tap any card to read the full case study.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <p className="mono text-[11px] tracking-[0.18em] uppercase text-[#8B7B6E] mr-2 hidden sm:block">
              {receipts.length} CHAPTERS · SCROLL →
            </p>
            <button
              data-testid="receipts-scroll-left"
              onClick={() => scrollBy(-1)}
              className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-[#EDEDED] hover:bg-white/5 transition-colors"
              aria-label="Scroll left"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              data-testid="receipts-scroll-right"
              onClick={() => scrollBy(1)}
              className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-[#EDEDED] hover:bg-white/5 transition-colors"
              aria-label="Scroll right"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Horizontal scroller */}
        <div
          ref={trackRef}
          className="no-scrollbar flex gap-5 overflow-x-auto pb-6 -mx-2 px-2 snap-x snap-mandatory"
        >
          {receipts.map((r, i) => {
            const tilts = ["-rotate-1", "rotate-1", "-rotate-2", "rotate-2", "-rotate-1", "rotate-1"];
            const tilt = tilts[i % tilts.length];
            return (
              <button
                key={r.id}
                type="button"
                data-testid={`receipt-${r.id}`}
                onClick={() => setActive(r)}
                className={`receipt snap-start shrink-0 w-[280px] md:w-[320px] px-6 py-5 ${tilt} hover:rotate-0 hover:-translate-y-1.5 transition-all duration-300 cursor-pointer text-left focus:outline-none focus:ring-2 focus:ring-[#FFD2A6]/60 focus:ring-offset-2 focus:ring-offset-[#121826]`}
              >
                {/* Header strip */}
                <div className="flex items-center justify-between mb-3 pb-3 border-b border-dashed border-black/20">
                  <span className="mono text-[10px] tracking-widest uppercase text-black/55">
                    {r.year}
                  </span>
                  <span
                    className="mono text-[10px] tracking-widest uppercase px-2 py-0.5 rounded-full font-bold"
                    style={{ background: r.color, color: "#121826" }}
                  >
                    {r.badge}
                  </span>
                </div>

                <div className="display text-[20px] md:text-[22px] text-[#121826] leading-tight mb-1">
                  {r.company}
                </div>
                <div className="mono text-[10px] tracking-wider uppercase text-black/60 mb-4">
                  {r.role}
                </div>

                <p className="text-[14px] leading-snug text-black/85 mb-5 min-h-[3.4em]">
                  {r.line}
                </p>

                {/* Total / metric line */}
                <div className="pt-3 border-t border-dashed border-black/20 flex items-end justify-between">
                  <span className="mono text-[10px] uppercase tracking-widest text-black/55">
                    Tap to read
                  </span>
                  <span
                    className="display text-[20px] leading-none"
                    style={{ color: "#121826" }}
                  >
                    {r.metric}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Detail dialog */}
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent
          data-testid="receipt-dialog"
          className="max-w-3xl max-h-[88vh] overflow-y-auto bg-[#0D0D0F] border-white/10 text-[#EDEDED] p-0"
        >
          {active && (
            <div>
              <div className="sticky top-0 z-10 bg-[#0D0D0F]/95 backdrop-blur border-b border-white/5 px-6 md:px-10 py-5 flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span
                      className="mono text-[10px] tracking-widest uppercase px-2 py-0.5 rounded-full font-bold"
                      style={{ background: active.color, color: "#121826" }}
                    >
                      {active.badge}
                    </span>
                    <span className="mono text-[11px] text-[#8B7B6E] tracking-wider">
                      {active.company} · {active.year}
                    </span>
                  </div>
                  <DialogTitle className="display text-[24px] md:text-[32px] tracking-[-0.01em] leading-tight text-[#FFF3E9]">
                    {active.fullTitle || active.company}
                  </DialogTitle>
                  <DialogDescription className="text-[14px] text-[#8A8A90] mt-1">
                    {active.tagline}
                  </DialogDescription>
                </div>
                <button
                  type="button"
                  onClick={() => setActive(null)}
                  data-testid="receipt-dialog-close"
                  className="shrink-0 w-9 h-9 rounded-full border border-white/10 flex items-center justify-center text-[#A1A1A6] hover:bg-white/5"
                  aria-label="Close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="px-6 md:px-10 py-8">
                <div className="flex flex-wrap gap-2 mb-8">
                  <Badge className="bg-white/5 text-[#FFD2A6] border-white/10 rounded-full font-normal text-[11px]">
                    {active.role}
                  </Badge>
                  <Badge
                    variant="outline"
                    className="border-white/10 text-[#A1A1A6] bg-transparent rounded-full font-normal text-[11px]"
                  >
                    {active.metric}
                  </Badge>
                </div>

                {active.summary && (
                  <p
                    className="text-[17px] leading-relaxed text-[#DADADF] mb-6 border-l-2 pl-5 serif-italic"
                    style={{ borderColor: active.color }}
                  >
                    {active.summary}
                  </p>
                )}

                {active.companyNote && (
                  <div className="mb-10 p-4 rounded-lg bg-white/[0.02] border border-white/5">
                    <div className="mono text-[10px] tracking-widest uppercase text-[#8B7B6E] mb-1.5">
                      Context
                    </div>
                    <p className="text-[13px] leading-relaxed text-[#9A9AA0]">
                      {active.companyNote}
                    </p>
                  </div>
                )}

                <div className="space-y-8">
                  {(active.sections || []).map((s, i) => (
                    <div key={s.heading} className="grid md:grid-cols-12 gap-4">
                      <div className="md:col-span-3">
                        <div className="mono text-[11px] text-[#8B7B6E] tracking-widest">
                          {String(i + 1).padStart(2, "0")}
                        </div>
                        <div
                          className="display text-[15px] mt-1"
                          style={{ color: active.color }}
                        >
                          {s.heading}
                        </div>
                      </div>
                      <p className="md:col-span-9 text-[15px] leading-relaxed text-[#B8B8BC]">
                        {s.body}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default Receipts;
